import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import {
  cancelSubscription,
  resubscribeSubscription,
  pollSubscriptionStatus,
} from '../features/subscription/services/subscriptionApi';
import PlanSwitcher from '../features/subscription/components/PlanSwitcher';
import { deleteAccount } from '../features/settings/services/accountApi';
import { COUNTRIES } from '../features/settings/countries';
import { PageContainer } from '../components/PageHeader';
import SEO from '../components/SEO';
import useDialogFocus from '../hooks/useDialogFocus';
import '../styles/settings.css';

/* Only controls with a real backend are built here. The mockup's Edit profile,
   Lesson pace, Notifications, Export-data, and Shared-courses count have no
   endpoint (frontend or backend) and are intentionally omitted rather than
   shown as toggles that persist nothing — see the design-system rollout ledger. */

const APPEARANCE_OPTIONS = [
  {
    value: 'LIGHT',
    label: 'Light',
    icon: (
      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
        <circle cx="8" cy="8" r="3.2" />
        <path d="M8 1.4v1.6M8 13v1.6M14.6 8H13M3 8H1.4M12.7 3.3l-1.1 1.1M4.4 11.6l-1.1 1.1M12.7 12.7l-1.1-1.1M4.4 4.4L3.3 3.3" />
      </svg>
    ),
  },
  {
    value: 'DARK',
    label: 'Dark',
    icon: (
      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" aria-hidden="true">
        <path d="M13.4 9.6A5.8 5.8 0 0 1 6.4 2.6a5.8 5.8 0 1 0 7 7z" />
      </svg>
    ),
  },
  {
    value: 'SYSTEM',
    label: 'System',
    icon: (
      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="1.8" y="2.6" width="12.4" height="8.6" rx="1.6" />
        <path d="M5.6 14h4.8" />
      </svg>
    ),
  },
];

const DIFFICULTY_OPTIONS = [
  { value: 'BEGINNER', label: 'Beginner' },
  { value: 'INTERMEDIATE', label: 'Intermediate' },
  { value: 'ADVANCED', label: 'Advanced' },
];

function titleCase(s) {
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

function initialsOf(name, email) {
  const source = String(name || email || 'U').trim();
  const parts = source.split(/\s+/);
  const letters = parts.map((p) => p[0]?.toUpperCase()).slice(0, 2).join('');
  return letters || 'U';
}

function Segmented({ ariaLabel, options, value, onChange, disabled }) {
  return (
    <div className="settings-segmented" role="radiogroup" aria-label={ariaLabel}>
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          role="radio"
          aria-checked={value === opt.value}
          disabled={disabled}
          onClick={() => onChange(opt.value)}
        >
          {opt.icon}
          {opt.label}
        </button>
      ))}
    </div>
  );
}

const ChevronRight = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="var(--text-tertiary)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6.4 3.6L10.8 8l-4.4 4.4" />
  </svg>
);

export default function SettingsPage() {
  const {
    user,
    logout,
    subscription,
    subscriptionLoading,
    isSubscribed,
    setSubscription,
    preferences,
    preferencesLoading,
    updatePreferences,
  } = useAuth();
  const navigate = useNavigate();

  const [err, setErr] = useState('');
  const [savedVisible, setSavedVisible] = useState(false);
  const savedTimer = useRef(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [resubscribeLoading, setResubscribeLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [deleteArmed, setDeleteArmed] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => () => { if (savedTimer.current) clearTimeout(savedTimer.current); }, []);

  // Focus-in, Tab-trap, Escape-to-close and focus-restore for the cancel-retention
  // modal all live in the shared dialog hook (same as parentPortal's Modal).
  const cancelDialogRef = useDialogFocus({
    active: showCancelModal,
    onEscape: () => setShowCancelModal(false),
  });

  const flashSaved = useCallback(() => {
    setSavedVisible(true);
    if (savedTimer.current) clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSavedVisible(false), 2200);
  }, []);

  const formatDate = (ts) =>
    ts ? new Date(ts * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A';

  const handlePref = async (key, value) => {
    if (preferences?.[key] === value) return; // no-op if unchanged
    setErr('');
    try {
      await updatePreferences({ [key]: value });
      flashSaved();
    } catch (e) {
      setErr('Failed to save preference. Please try again.');
    }
  };

  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/');
    } catch { /* logout swallows its own errors; navigation is best-effort */ }
  };

  const confirmCancel = async () => {
    setShowCancelModal(false);
    setCancelLoading(true);
    setErr('');
    try {
      await cancelSubscription();
      const status = await pollSubscriptionStatus((s) => s.cancelAtPeriodEnd === true, { maxAttempts: 10, intervalMs: 1500 });
      setSubscription(status);
    } catch (e) {
      setErr(e.message === 'Timeout waiting for subscription update'
        ? 'Cancellation processing. Please refresh to see updated status.'
        : 'Failed to cancel subscription. Please try again.');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleResubscribe = async () => {
    if (!window.confirm('Resume your subscription? Your billing will continue as normal.')) return;
    setResubscribeLoading(true);
    setErr('');
    try {
      await resubscribeSubscription();
      const status = await pollSubscriptionStatus((s) => s.cancelAtPeriodEnd === false, { maxAttempts: 10, intervalMs: 1500 });
      setSubscription(status);
    } catch (e) {
      setErr(e.message === 'Timeout waiting for subscription update'
        ? 'Reactivation processing. Please refresh to see updated status.'
        : (e?.message || 'Failed to resubscribe. Please try again.'));
    } finally {
      setResubscribeLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    setErr('');
    try {
      await deleteAccount();
      await logout();
      navigate('/');
    } catch (e) {
      setErr(e?.message || 'Failed to delete account. Please try again.');
      setDeleteLoading(false);
      setDeleteArmed(false);
    }
  };

  const cancelling = subscription?.cancelAtPeriodEnd;

  return (
    <>
      <SEO title="Settings" description="Manage your Graspd account, subscription, and preferences." path="/settings" noindex />
      <PageContainer>
        <div className="settings-head">
          <div>
            <h1 className="settings-title">Settings</h1>
            <p className="settings-sub">Changes save as you make them.</p>
          </div>
          <span className={`settings-saved ${savedVisible ? 'is-visible' : ''}`} aria-live="polite">
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <circle cx="8" cy="8" r="7" fill="var(--accent-primary)" />
              <path d="M4.8 8.2l2.1 2.1 4.3-4.4" stroke="var(--text-inverted)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Saved
          </span>
        </div>

        {err && <div className="settings-error">{err}</div>}

        <div className="settings-grid-2">
        {/* Account */}
        <section className="settings-section">
          <h2 className="settings-section-title">Account</h2>
          <div className="settings-panel">
            <div className="settings-account">
              <span className="settings-avatar">
                {user?.photoURL
                  ? <img src={user.photoURL} alt="" referrerPolicy="no-referrer" />
                  : initialsOf(user?.displayName, user?.email)}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p className="settings-account-name">{user?.displayName || 'User'}</p>
                <p className="settings-account-email">{user?.email}</p>
              </div>
              <button type="button" className="settings-btn settings-btn--ghost" onClick={handleSignOut}>
                Sign out
              </button>
            </div>
          </div>
        </section>

        {/* Plan */}
        <section className="settings-section">
          <h2 className="settings-section-title">Plan</h2>
          <div className="settings-panel">
            {subscriptionLoading ? (
              <div className="settings-plan" aria-busy="true">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p className="settings-plan-name" style={{ color: 'var(--text-tertiary)' }}>Loading plan…</p>
                </div>
              </div>
            ) : isSubscribed ? (
              <>
                <div className="settings-plan">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="settings-plan-title">
                      <span className="settings-plan-name">{titleCase(subscription?.accountType) || 'Subscribed'}</span>
                      <span className="settings-badge">{subscription?.interval === 'month' ? 'Monthly' : 'Annual'}</span>
                    </div>
                    <p className="settings-plan-meta">
                      {cancelling
                        ? `Ends ${formatDate(subscription?.currentPeriodEnd)}`
                        : `Renews ${formatDate(subscription?.currentPeriodEnd)}`}
                      {/* PlanSwitcher renders an inline " · switch to annual"
                          link (or null); it belongs in the meta line, not a
                          separate column, or its leading separator floats. */}
                      {!cancelling && <PlanSwitcher />}
                    </p>
                  </div>
                </div>
                <div className="settings-footnote">
                  <p style={{ flex: 1, margin: 0 }}>
                    {cancelling
                      ? 'Your plan is set to cancel. Resubscribe to keep your access from lapsing.'
                      : 'Annual billing saves two months. Cancelling keeps your access until the current period ends.'}
                  </p>
                  {cancelling ? (
                    <button type="button" className="settings-btn settings-btn--primary" onClick={handleResubscribe} disabled={resubscribeLoading}>
                      {resubscribeLoading ? 'Reactivating…' : 'Resubscribe'}
                    </button>
                  ) : (
                    <button type="button" className="settings-btn settings-btn--danger" onClick={() => setShowCancelModal(true)} disabled={cancelLoading}>
                      {cancelLoading ? 'Canceling…' : 'Cancel plan'}
                    </button>
                  )}
                </div>
              </>
            ) : (
              <div className="settings-plan">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="settings-plan-title">
                    <span className="settings-plan-name">No active plan</span>
                  </div>
                  <p className="settings-plan-meta">Subscribe to unlock courses, G-Score, and analytics.</p>
                </div>
                <Link to="/dashboard" className="settings-btn settings-btn--primary">Subscribe</Link>
              </div>
            )}
          </div>
        </section>
        </div>

        {/* Preferences */}
        <section className="settings-section">
          <h2 className="settings-section-title settings-section-title--tight">Preferences</h2>
          <p className="settings-section-desc">
            These shape how new lessons are written. Existing lessons keep the settings they were generated with.
          </p>
          <div className="settings-panel settings-pref-grid">
            <div className="settings-pref">
              <p className="settings-row-label">Appearance</p>
              <p className="settings-row-hint">System follows your device setting.</p>
              <div className="settings-pref-control">
                <Segmented
                  ariaLabel="Appearance"
                  options={APPEARANCE_OPTIONS}
                  value={preferences?.theme}
                  disabled={preferencesLoading}
                  onChange={(v) => handlePref('theme', v)}
                />
              </div>
            </div>

            <div className="settings-pref">
              <p className="settings-row-label">Preferred difficulty</p>
              <p className="settings-row-hint">The level new courses start at. You can override it per course.</p>
              <div className="settings-pref-control">
                <Segmented
                  ariaLabel="Preferred difficulty"
                  options={DIFFICULTY_OPTIONS}
                  value={preferences?.defaultDifficulty}
                  disabled={preferencesLoading}
                  onChange={(v) => handlePref('defaultDifficulty', v)}
                />
              </div>
            </div>

            <div className="settings-pref">
              <p className="settings-row-label">Country</p>
              <p className="settings-row-hint">Sets your language variant, worked examples and units.</p>
              <div className="settings-pref-control">
                <div className="settings-select">
                  <select
                    aria-label="Country"
                    value={preferences?.country || ''}
                    disabled={preferencesLoading}
                    onChange={(e) => handlePref('country', e.target.value)}
                  >
                    <option value="" disabled>Select a country</option>
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>{c.name}</option>
                    ))}
                  </select>
                  <svg className="settings-select-caret" width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true">
                    <path d="M2.4 4.4L6 8l3.6-3.6" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="settings-grid-2">
        {/* Sharing and supervision */}
        <section className="settings-section">
          <h2 className="settings-section-title">Sharing and supervision</h2>
          <div className="settings-panel">
            <Link to="/settings/sharing" className="settings-row-link">
              <span className="settings-row-text">
                <span className="settings-row-label" style={{ display: 'block' }}>Parent supervision</span>
                <span className="settings-row-hint" style={{ display: 'block' }}>
                  Give a parent, tutor, or mentor a read-only view of your courses and progress.
                </span>
              </span>
              <span className="settings-row-control"><ChevronRight /></span>
            </Link>
          </div>
        </section>

        {/* Your data */}
        <section className="settings-section">
          <h2 className="settings-section-title">Your data</h2>
          <div className="settings-panel">
            <div className="settings-row">
              <div className="settings-row-text">
                <p className="settings-row-label">Delete account</p>
                <p className="settings-row-hint">
                  Cancels your subscription, anonymizes your data, and removes your login. This cannot be undone.
                </p>
              </div>
              <div className="settings-row-control">
                {deleteArmed && <span className="settings-confirm">Are you sure?</span>}
                <button
                  type="button"
                  className="settings-btn settings-btn--danger"
                  onClick={deleteArmed ? handleDelete : () => setDeleteArmed(true)}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? 'Deleting…' : deleteArmed ? 'Yes, delete everything' : 'Delete account'}
                </button>
                {deleteArmed && !deleteLoading && (
                  <button type="button" className="settings-btn settings-btn--ghost" onClick={() => setDeleteArmed(false)}>
                    Keep account
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>
        </div>

        <div className="settings-foot">
          <p style={{ margin: 0 }}>
            Need help? <a href="mailto:contact@graspd.ai">contact@graspd.ai</a>
          </p>
          <nav>
            <Link to="/privacy">Privacy policy</Link>
            <Link to="/terms">Terms of service</Link>
          </nav>
        </div>
      </PageContainer>

      {showCancelModal && createPortal(
        <div
          className="settings-modal-scrim"
          role="presentation"
          onClick={() => setShowCancelModal(false)}
        >
          <div
            ref={cancelDialogRef}
            className="settings-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="settings-cancel-title"
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="settings-modal-icon" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--warning)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <h3 id="settings-cancel-title" className="settings-modal-title">Before you go…</h3>
            <p className="settings-modal-body">
              You'll keep full access until {formatDate(subscription?.currentPeriodEnd)}. If something isn't working for
              you, we'd genuinely like to hear it — reach us at <a href="mailto:contact@graspd.ai">contact@graspd.ai</a>.
            </p>
            <div className="settings-modal-actions">
              <button type="button" className="settings-btn settings-btn--primary" onClick={() => setShowCancelModal(false)}>
                Keep my plan
              </button>
              <button type="button" className="settings-btn settings-btn--danger" onClick={confirmCancel}>
                Cancel anyway
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
